import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Loader2 } from "lucide-react";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { createVoiceProfile, listVoices, updateVoiceProfile } from "@/lib/studioApi";
import { safeApiError } from "@/lib/safeErrorText";

const EMPTY = { name: "", gender: "f", description: "", style: "", cloned_voice_id: null };
const inputCls = "w-full rounded-xl border border-white/10 bg-black/40 px-3 py-2.5 text-sm text-white placeholder:text-white/30 focus:border-blue-400/60 focus:outline-none";

function Field({ label, hint, children }) {
  return (
    <label className="block">
      <span className="mb-1 block font-mono text-[10px] uppercase tracking-[0.16em] text-white/60">{label}</span>
      {children}
      {hint ? <span className="mt-1 block text-[11px] text-white/40">{hint}</span> : null}
    </label>
  );
}

/** Create / edit a signature voice profile (name, how it sings, optional linked recorded voice). */
export default function VoiceProfileDialog({ open, profile, onOpenChange, onSaved }) {
  const [form, setForm] = useState(EMPTY);
  const [voices, setVoices] = useState(null);
  const [saving, setSaving] = useState(false);
  useEffect(() => {
    if (!open) return;
    setForm(profile ? { name: profile.name, gender: profile.gender || "m", description: profile.description || "", style: profile.style || "", cloned_voice_id: profile.cloned_voice_id || null } : EMPTY);
    if (voices === null) listVoices().then(setVoices).catch(() => setVoices([]));
  }, [open, profile, voices]);
  const set = (patch) => setForm((f) => ({ ...f, ...patch }));

  const save = async () => {
    setSaving(true);
    try {
      const saved = profile ? await updateVoiceProfile(profile.id, form) : await createVoiceProfile(form);
      toast.success(profile ? "Voice profile saved" : "Guest voice added");
      onSaved(saved);
    } catch (e) { toast.error("Couldn't save the voice profile", { description: safeApiError(e) }); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="voice-profile-dialog" className="max-h-[90vh] overflow-y-auto border-white/10 bg-[#0b0f22] text-white sm:max-w-lg">
        <DialogHeader>
          <DialogTitle className="font-display text-xl">{profile ? "Edit voice profile" : "Add a guest voice"}</DialogTitle>
          <DialogDescription className="text-white/55">Describe how this voice sings — ELPM uses it whenever you pick the profile as lead or duet partner.</DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <Field label="Name"><input data-testid="voice-profile-name" value={form.name} maxLength={80} onChange={(e) => set({ name: e.target.value })} placeholder="e.g. Sister Grace — Worship Soprano" className={inputCls} /></Field>
          <Field label="Voice">
            <div role="radiogroup" className="inline-flex rounded-full border border-white/10 bg-black/40 p-1">
              {[["m", "Male"], ["f", "Female"]].map(([k, label]) => (
                <button key={k} type="button" role="radio" aria-checked={form.gender === k} data-testid={`voice-profile-gender-${k}`} onClick={() => set({ gender: k })} className={`rounded-full px-4 py-1.5 font-mono text-[11px] uppercase tracking-wider ${form.gender === k ? "bg-white/[0.14] text-white" : "text-white/50"}`}>{label}</button>
              ))}
            </div>
          </Field>
          <Field label="How it sounds" hint="Shown on the voice card."><textarea data-testid="voice-profile-description" rows={3} maxLength={600} value={form.description} onChange={(e) => set({ description: e.target.value })} placeholder="Warm, bright soprano with soaring worship runs…" className={inputCls} /></Field>
          <Field label="Singing style for the music engine" hint="Short, comma-separated words work best. Leave empty to use the description."><textarea data-testid="voice-profile-style" rows={3} maxLength={400} value={form.style} onChange={(e) => set({ style: e.target.value })} placeholder="bright female soprano, gospel runs, airy head voice" className={inputCls} /></Field>
          <Field label="Linked recorded voice (optional)" hint="Used by “My Recorded Voice” when this profile is the lead.">
            <select data-testid="voice-profile-clone" value={form.cloned_voice_id || ""} onChange={(e) => set({ cloned_voice_id: e.target.value || null })} className={inputCls}>
              <option value="">None</option>
              {(voices || []).map((v) => <option key={v.voice_id} value={v.voice_id}>{v.name}</option>)}
            </select>
          </Field>
        </div>
        <div className="mt-2 flex justify-end gap-2">
          <button type="button" data-testid="voice-profile-cancel" onClick={() => onOpenChange(false)} className="rounded-full border border-white/15 px-5 py-2.5 font-display text-sm text-white/80 hover:bg-white/[0.06]">Cancel</button>
          <button type="button" data-testid="voice-profile-save" onClick={save} disabled={saving || !form.name.trim()} className="inline-flex items-center gap-2 rounded-full bg-gradient-to-r from-blue-500 to-violet-600 px-6 py-2.5 font-display text-sm font-semibold disabled:opacity-40">
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : null} Save
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
